import { useState } from 'react';
import {
  CaretDownIcon,
  CaretUpIcon,
  ChatCircleDotsIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ChatMarkdown } from '@/components/common/chat';
import {
  actionIcon,
  turnActionLabel,
} from '@/components/common/agentActionFormatting';

export interface OperatorTurnCardProps {
  reasoning: string | null;
  actions: ReadonlyArray<{ id: string; action: string; detail: string | null }>;
  screenshotDataUrl: string | null;
  /** Starting expand state for the reasoning block — the interview page passes `true` for
   * whichever turn was the latest when it mounted, so the human sees the current turn's
   * thinking without an extra click, while older turns stay collapsed. */
  defaultExpanded?: boolean;
}

/** One agent "turn" — a collapsible reasoning block, an ordered checklist of the concrete
 * actions taken, and the turn's screenshot (if any). Replaces the plain-text bubble the
 * interview chat used to render per reasoning entry. */
export function OperatorTurnCard({
  reasoning,
  actions,
  screenshotDataUrl,
  defaultExpanded = false,
}: OperatorTurnCardProps) {
  const gt = useGT();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <div className="w-full max-w-[85%] space-y-1.5 rounded-lg border border-border bg-muted/30 px-3 py-2">
      {reasoning && (
        <div>
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="flex w-full items-center gap-1.5 text-left text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChatCircleDotsIcon className="h-3.5 w-3.5 shrink-0" />
            <T context="collapsible reasoning header in an operator interview turn card">
              Reasoning
            </T>
            {expanded ? (
              <CaretUpIcon className="h-3 w-3 shrink-0" weight="bold" />
            ) : (
              <CaretDownIcon className="h-3 w-3 shrink-0" weight="bold" />
            )}
          </button>
          {expanded && (
            <div className="mt-1 break-words text-sm text-foreground">
              <ChatMarkdown>{reasoning}</ChatMarkdown>
            </div>
          )}
        </div>
      )}
      {actions.length > 0 && (
        <ul className="space-y-1">
          {actions.map((action) => {
            const ActionIcon = actionIcon(action.action);
            return (
              <li
                key={action.id}
                className="flex items-start gap-2 text-[13px]"
              >
                <ActionIcon
                  className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                  weight="bold"
                />
                <span className="shrink-0 text-foreground">
                  {turnActionLabel(action.action, gt)}
                </span>
                {action.detail && (
                  <code className="min-w-0 break-words rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground/80">
                    {action.detail}
                  </code>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {screenshotDataUrl && (
        <div>
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="group relative inline-block cursor-pointer"
          >
            <img
              src={screenshotDataUrl}
              alt={gt('Page screenshot', {
                context:
                  'alt text for an operator turn card screenshot thumbnail',
              })}
              className="h-16 w-24 rounded-lg border border-border object-cover"
            />
          </button>
          <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
            <DialogContent
              className="max-w-fit sm:max-w-fit max-h-[90vh] p-2"
              showCloseButton
            >
              <DialogTitle className="sr-only">
                <T context="screen reader title for an operator turn card screenshot preview dialog">
                  Screenshot preview
                </T>
              </DialogTitle>
              <img
                src={screenshotDataUrl}
                alt={gt('Page screenshot', {
                  context:
                    'alt text for an operator turn card screenshot preview',
                })}
                className="max-w-[85vw] max-h-[82vh] object-contain"
              />
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
